/**
 * connectLane — the ONE answer to "how does this app actually sign in?"
 * (2026-09-02, owner report: "some apps show loading then disappear and an
 * error message displays").
 *
 * THE BUG THIS EXISTS TO KILL
 * ---------------------------
 * Composio's catalog is ~1,448 toolkits, but only ~122 of them (8%) carry
 * `composio_managed_auth_schemes`. The OAuth mint
 * (functions/src/composio/composioService.ts initiateConnection) can ONLY
 * work for those: for every other toolkit it throws
 * "<slug> does not support Composio managed authentication", the route
 * answers 502, and launchConnectPopup closes the popup it had already opened
 * — the "opening the sign-in…" window that vanishes, followed by an error
 * banner. Every surface offered that doomed button:
 *
 *   - onboarding's recommended rows and its "connect your apps" catalog modal
 *     (DocuSign is recommended in 11 of the 18 industry lists, Xero in
 *     Finance, Shopify in E-commerce — none of them one-click),
 *   - the integrations page (the primary Connect button rendered even for the
 *     tiles it labelled 'Assisted'),
 *   - the in-chat connect card (any catalog name match got a connect button).
 *
 * THE CONTRACT
 * ------------
 * A toolkit's lane is derived from catalog metadata the client ALREADY has —
 * no extra round trip, no per-app table to maintain, and every future
 * catalog addition is classified the day it appears:
 *
 *   'oauth'   managed auth, nothing to type first — the one-click mint works.
 *   'setup'   managed auth, but required initiation values first (WhatsApp's
 *             WABA id). Still the direct lane; the dialog collects them.
 *   'browser' NO managed auth. The direct mint CANNOT work. Prest0n signs in
 *             on its own computer instead (connectCustomFlow) — the lane that
 *             already existed for off-catalog apps.
 *   'none'    no_auth toolkits: nothing to sign into at all.
 *
 * Surfaces must route on the lane BEFORE offering a direct connect. The
 * engine (connectPopupFlow.launchConnectPopup) enforces it as the choke
 * point, and the server's typed `unsupported_auth` rejection is the
 * belt-and-braces for a client whose catalog copy is cold or stale.
 */

import { peekToolkitBySlug, TOOLKITS_WITH_SETUP_FIELDS } from './ComposioClient';
import type { ComposioToolkit } from './ComposioClient';

export type ConnectLane = 'oauth' | 'setup' | 'browser' | 'none';

/** Shape the classifier needs — a full ComposioToolkit satisfies it. */
export type ConnectLaneInput = Pick<
  ComposioToolkit,
  'slug' | 'composio_managed_auth_schemes' | 'auth_schemes' | 'no_auth'
> & Partial<Pick<ComposioToolkit, 'platform_auth_schemes'>>;

/**
 * True when the direct OAuth mint is possible at all: the connector manages the sign-in, or Prest0's own app
 * with the provider does (`platform_auth_schemes`, which the server adds to the catalog only once that app
 * serves people — functions/src/composio/platformAuth.ts, ES-1a).
 */
export function hasManagedAuth(
  tk: Pick<ConnectLaneInput, 'composio_managed_auth_schemes'> & Partial<Pick<ConnectLaneInput, 'platform_auth_schemes'>>,
): boolean {
  const managed = Array.isArray(tk.composio_managed_auth_schemes) && tk.composio_managed_auth_schemes.length > 0;
  const platform = Array.isArray(tk.platform_auth_schemes) && tk.platform_auth_schemes.length > 0;
  return managed || platform;
}

/**
 * Classify a toolkit. Managed auth decides the direct lanes; everything else
 * — API-key-only toolkits, unmanaged OAuth, SAML, DCR — is the browser lane,
 * because none of them can complete through connectedAccounts.create.
 */
export function connectLaneFor(tk: ConnectLaneInput): ConnectLane {
  if (hasManagedAuth(tk)) {
    return TOOLKITS_WITH_SETUP_FIELDS.has(String(tk.slug || '').toLowerCase())
      ? 'setup'
      : 'oauth';
  }
  // no_auth is checked AFTER managed auth: a toolkit that offers both is
  // still connectable directly, and the direct connection is the better one.
  if (tk.no_auth) return 'none';
  return 'browser';
}

/**
 * Key schemes the paste-a-key lane can execute — the SAME literal the server
 * uses (functions/src/composio/keyLane.ts KEY_SCHEMES).
 */
const KEY_SCHEMES = ['API_KEY', 'BASIC', 'BEARER_TOKEN'];

/**
 * Does this app plausibly accept a pasted key?
 *
 * THE MEASUREMENT BEHIND THIS (live catalog walk, run 33731730326):
 *   - `auth_config_details` — the field the server's `resolveKeyScheme` reads —
 *     is present on 0 of 1,501 list items. The catalog copy a surface holds
 *     CANNOT answer the question exactly, and deriving it in the walk would
 *     cost ~1,501 retrieves per refresh.
 *   - `auth_schemes` IS present, on 1,500 of 1,501. On a 24-toolkit sample
 *     spread across the walk it agreed with the retrieved truth 24 times out
 *     of 24 — 17 correct yes, 7 correct no, zero misses either way. 1,249 of
 *     1,501 toolkits declare a key scheme.
 *
 * So this is a PREDICTION, not the answer, and it is deliberately only used to
 * OFFER the path. The exact fields — and the final yes/no — come from the
 * server when the user takes it, which is the same round trip that would have
 * been needed anyway. A toolkit this predicts wrongly costs one round trip and
 * falls back to the browser lane; it never dead-ends. That fallback is the
 * design: a 24-app sample is not a proof for 1,249.
 *
 * It never overrides a managed one-click sign-in. Pasting a secret is worse
 * than signing in when signing in is available, so `connectLaneFor` still
 * decides the lane and this only widens what the browser lane's card OFFERS.
 */
export function toolkitDeclaresKeyScheme(
  tk: (Pick<ConnectLaneInput, 'auth_schemes' | 'composio_managed_auth_schemes' | 'no_auth'>
    & Partial<Pick<ConnectLaneInput, 'platform_auth_schemes'>>) | null | undefined,
): boolean {
  if (!tk) return false;
  if (hasManagedAuth(tk)) return false;
  if (tk.no_auth) return false;
  return (Array.isArray(tk.auth_schemes) ? tk.auth_schemes : [])
    .map((s) => String(s || '').toUpperCase())
    .some((s) => KEY_SCHEMES.includes(s));
}

/**
 * The prediction for a slug, from the catalog copy the client already holds.
 * SYNCHRONOUS, like peekConnectLane and for the same reason — it is read while
 * a card is being built. A cold catalog answers `false`: the card simply does
 * not offer the shortcut, which is today's behaviour and never a broken offer.
 */
export function peekToolkitDeclaresKeyScheme(toolkitSlug: string): boolean {
  try {
    return toolkitDeclaresKeyScheme(peekToolkitBySlug(toolkitSlug));
  } catch {
    return false;
  }
}

/** The two lanes the direct OAuth popup can serve. */
export function laneIsDirect(lane: ConnectLane): boolean {
  return lane === 'oauth' || lane === 'setup';
}

/**
 * Lane for a slug, resolved from the catalog copy the client already holds —
 * SYNCHRONOUS by contract. The connect popup must be opened inside the
 * click's own call stack (see connectPopupFlow's module docblock), so this
 * can never await a fetch. 'unknown' means the catalog is not warm yet: the
 * caller proceeds with the direct attempt and relies on the server's
 * `unsupported_auth` rejection to fall back.
 */
export function peekConnectLane(toolkitSlug: string): ConnectLane | 'unknown' {
  try {
    const tk = peekToolkitBySlug(toolkitSlug);
    return tk ? connectLaneFor(tk) : 'unknown';
  } catch {
    // A classification read must NEVER break a connect click. Anything that
    // goes wrong here (private-mode storage, a stub without this export)
    // degrades to the cold-catalog path, where the server's typed
    // `unsupported_auth` still gives the right verdict one round trip later.
    return 'unknown';
  }
}

/**
 * House copy for a lane, in the app's own name. D9 (2026-08-24) permits
 * code-authored failure/guidance copy; the house voice is lowercase with
 * "Prest0n" always capitalized (KeepCase is only needed where a CSS
 * lowercase transform applies — these strings are rendered literally).
 */
export function laneCtaLabel(lane: ConnectLane): string {
  switch (lane) {
    case 'browser':
      return 'sign in with Prest0n';
    case 'none':
      return 'no sign-in needed';
    case 'setup':
      return 'set up';
    default:
      return 'connect';
  }
}

export function laneBadgeLabel(lane: ConnectLane): string {
  switch (lane) {
    case 'browser':
      return 'Prest0n signs in';
    case 'none':
      return 'No sign-in';
    case 'setup':
      return 'Quick setup';
    default:
      return '1-Click';
  }
}

/** One-line explanation of why this app takes the browser lane. */
export function laneExplainer(lane: ConnectLane, appName: string): string {
  switch (lane) {
    case 'browser':
      return `${appName} has no one-click sign-in — Prest0n opens it on its own computer and you sign in once.`;
    case 'none':
      return `${appName} needs no sign-in — Prest0n can already use it.`;
    case 'setup':
      return `${appName} asks for a quick setup detail before its sign-in can start.`;
    default:
      return `${appName} signs in one click.`;
  }
}

/**
 * Route an app into Prest0n's browser sign-in — the ONE call every surface
 * makes when a lane turns out not to be direct. Dynamic import keeps the
 * custom-flow chunk out of the surfaces' bundles (the same idiom onboarding
 * and the integrations panel already use).
 *
 * `chatFirst` prefers the chat concierge (the 2026-08-18 directive: every
 * non-direct connection goes through the chat interface from any surface)
 * and falls back to the in-page sign-in surface when no chat is mounted, so
 * the lane never dead-ends.
 */
export async function routeToBrowserLane(opts: {
  appName: string;
  toolkitSlug?: string;
  surface?: string;
  signInUrl?: string;
  workspaceAccountId?: string;
  chatFirst?: boolean;
  /** The sign-in catalog holds this app (a legal row's `signInName`): the window can open straight away. */
  signInPageKnown?: boolean;
  /** The press came from a chat card: a known page opens from that conversation, no second thread. */
  inConversation?: boolean;
}): Promise<void> {
  if (opts.chatFirst) {
    // ONE flow on every surface (slice E, Miles's ruling R4): a sign-in page that is known opens straight
    // away, beside a docked thread holding one line; an unknown one goes to the chat concierge first.
    // browserSignInStart.ts owns the decision and the funnel log.
    const { startBrowserSignIn } = await import('./browserSignInStart');
    await startBrowserSignIn({
      appName: opts.appName,
      ...(opts.toolkitSlug ? { toolkitSlug: opts.toolkitSlug } : {}),
      ...(opts.surface ? { surface: opts.surface } : {}),
      ...(opts.signInUrl ? { signInUrl: opts.signInUrl } : {}),
      ...(opts.workspaceAccountId ? { workspaceAccountId: opts.workspaceAccountId } : {}),
      ...(opts.signInPageKnown ? { signInPageKnown: true } : {}),
      ...(opts.inConversation ? { inConversation: true } : {}),
    });
    return;
  }
  const mod = await import('./connectCustomFlow');
  const { logConnectFunnel } = await import('./connectPopupFlow');
  logConnectFunnel({
    event: 'browser_lane_routed',
    ...(opts.toolkitSlug ? { toolkitSlug: opts.toolkitSlug } : {}),
    ...(opts.surface ? { surface: opts.surface } : {}),
  });
  await mod.beginCustomConnectFlow({
    appName: opts.appName,
    ...(opts.signInUrl ? { signInUrl: opts.signInUrl } : {}),
    ...(opts.workspaceAccountId ? { workspaceAccountId: opts.workspaceAccountId } : {}),
  });
}
